import { Tooltip } from "@mui/material";
import clsx from "clsx";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";

import { SVG_MUI } from "@ryde/assets";
import {
  isAdministrativeModeAtom,
  priceModeAtom,
  profilePermissionsSelector,
  RoleNameEnum,
  SuperAdminPriceMode,
  useSystemParams,
} from "@ryde/shared";

import { HeaderSupplierPriceIndicatorStyled } from "./header-supplier-price-indicator.styled";

export const HeaderSupplierPriceIndicator: FC = () => {
  const { t } = useTranslation();
  const isAdministrativeMode = useRecoilValue(isAdministrativeModeAtom);
  const priceMode = useRecoilValue(priceModeAtom);
  const roleDataItem = useRecoilValue(profilePermissionsSelector);
  const systemParams = useSystemParams();
  const isSupplierRole = roleDataItem?.role === RoleNameEnum.Supplier;
  const isSupplierPriceRatesEnabled =
    systemParams?.enableSupplierPriceRates === true;

  const { shouldShow, tooltipText, isRed } = useMemo(() => {
    if (!isSupplierPriceRatesEnabled) {
      return {
        shouldShow: false,
        tooltipText: undefined,
        isRed: false,
      };
    }

    // If admin mode is enabled and mode is Supplier or Both - show red briefcase with tooltip (priority)
    if (
      isAdministrativeMode &&
      (priceMode === SuperAdminPriceMode.Supplier ||
        priceMode === SuperAdminPriceMode.Both)
    ) {
      let modeLabel = "";
      if (priceMode === SuperAdminPriceMode.Supplier) {
        modeLabel = t("general.SUPPLIER_PRICES");
      } else if (priceMode === SuperAdminPriceMode.Both) {
        modeLabel = t("general.CLIENT_AND_SUPPLIER_PRICES");
      }

      return {
        shouldShow: true,
        tooltipText: `Admin mode: ${modeLabel}`,
        isRed: true,
      };
    }

    // If supplier role - show black briefcase without tooltip
    if (isSupplierRole) {
      return {
        shouldShow: true,
        tooltipText: undefined,
        isRed: false,
      };
    }

    return {
      shouldShow: false,
      tooltipText: undefined,
      isRed: false,
    };
  }, [
    isSupplierRole,
    isAdministrativeMode,
    priceMode,
    isSupplierPriceRatesEnabled,
    t,
  ]);

  if (!shouldShow) {
    return null;
  }

  const icon = (
    <HeaderSupplierPriceIndicatorStyled className={clsx({ isRed })}>
      <SVG_MUI.BriefcaseOutline2SVG />
    </HeaderSupplierPriceIndicatorStyled>
  );

  if (tooltipText) {
    return <Tooltip title={tooltipText}>{icon}</Tooltip>;
  }

  return icon;
};
